import { isEmpty } from '@s-libs/micro-dash';
import { AnyStep, StepData } from '../../@types';

/**
 * Given a list of branches, returns the maximum number of steps in any of the branches.
 * @param {StepData[][]} branches - The branches of the wizard
 * @param {StepData[]} stack - The current stack of steps
 * @returns {Number} The maximum number of steps in any of the branches
 */
const getMaxRoute = (branches: StepData[][], stack: StepData[]): number => {
  // Get a list of the stepnames of the current stack excluding the interstitial step
  const stackNames = stack
    .map((step) => step.stepName)
    .filter((stepName) => stepName !== 'interstitial');

  // Filter out any steps that are not countable and then filter out any branches that do not match the current stack in exact order
  const filteredBranches = branches
    .map((branch) => branch.filter(isCountable))
    .filter((branch) => {
      const branchKeys = branch.map((step) => step.stepName);
      return stackNames.every((key, index) => key === branchKeys[index]);
    });

  // Get the maximum length of the branches
  return Math.max(...filteredBranches.map((branch) => branch.length));
};

const isCountable = (step: StepData): boolean => {
  return !step._modelApiKey.includes('action') && step._modelApiKey !== 'panel_interstitial';
};

const terminatingBranchingStep = (step: AnyStep): boolean => {
  return isEmpty(step.conditions) || !!step.stepNameElse;
};

/**
 * Returns true if the given step terminates the wizard and the user cannot go forward.
 * @param {AnyStep} step - The step to check
 * @returns {Boolean}
 */
const terminatingStep = (step: AnyStep): boolean => {
  if (step._modelApiKey === 'action_redirect' && isEmpty(step.conditions)) {
    // redirects cannot proceed
    return true;
  }
  if (
    !step.action &&
    step._modelApiKey !== 'panel_interstitial' &&
    !step.backButton &&
    !step.continueButton &&
    !step.inputFields &&
    !step.stepNameTo
  ) {
    // content panels with no buttons or form controls cannot proceed
    return true;
  }
  return false;
};

/**
 * Given a list of steps, returns all the possible branches of the wizard.
 * @param {AnyStep[]} steps - The steps of the form
 * @param {AnyStep[]} currentBranch - The current branch of the wizard
 * @returns {AnyStep[][]} An array of arrays of steps, being all the different branches.
 */
const getBranchesRecursive = (steps: AnyStep[], currentBranch: AnyStep[]): AnyStep[][] => {
  const branches = [];
  for (const step of steps) {
    currentBranch.push(step);
    if (step._modelApiKey === 'action_go_to') {
      branches.push(...processGoToAction(step, steps, currentBranch));
      branches.push(...processGoToAction(step, steps, currentBranch, 'stepNameElse'));
      if (terminatingBranchingStep(step)) {
        // if go-to action is forced (not conditional), or we have an else condition, quit the loop
        // and do not add the current stack to the list of branches
        return branches;
      }
    }
    // if for any reason we cannot proceed to the next step, then break out here
    if (terminatingStep(step)) {
      break;
    }
  }
  branches.push(currentBranch);
  return branches;
};

const processGoToAction = (
  step: AnyStep,
  steps: AnyStep[],
  currentBranch: AnyStep[],
  property: string = 'stepNameTo'
): AnyStep[][] => {
  const stepNameTo = step[property];
  if (!stepNameTo) {
    return [];
  }
  // catch potential recursion exceptions
  if (currentBranch.length > 30) {
    throw new RangeError('Potential infinite recursion');
  }
  // process the go-to action branch and add it to the total found branches
  const branchIndex = steps.findIndex((dest) => dest.stepName === stepNameTo);
  return getSubBranches(steps, branchIndex, currentBranch);
};

const getSubBranches = (
  steps: AnyStep[],
  branchIndex: number,
  currentBranch: AnyStep[]
): AnyStep[][] => {
  if (branchIndex === -1) {
    return [];
  }
  return getBranchesRecursive(steps.slice(branchIndex), [...currentBranch]);
};

/**
 * Given an array of steps, parses them to extract all potential branches.
 * @param {Array} steps - The step fields of the form
 * @returns {Array[]} An array of arrays of steps, being all the different branches.
 */
const getBranches = (steps: AnyStep[]): AnyStep[][] => {
  try {
    return getBranchesRecursive(Array.from(steps), []);
  } catch (err) {
    if (err instanceof RangeError) {
      return [];
    }
  }
};

export { getBranches, getMaxRoute, getSubBranches, processGoToAction, terminatingStep };
